import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Person, 
  Visibility,
  Group
} from '@mui/icons-material';
import apiService from '../../services/apiService';
import { useToast } from '../common/ToastProvider';

function OrganizationUsersCard({ organizationId }) {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOrganizationUsers();
  }, [organizationId]);

  const fetchOrganizationUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await apiService.fetchOrganizationUsers(organizationId);
      
      if (result.success) {
        setUsers(result.data || []);
      } else {
        setError(result.error);
        showError(`Failed to load project users: ${result.error}`, 'Error');
      }
    } catch (err) {
      setError('Failed to load organization users');
      showError('Failed to load project users. Please try again.', 'Error');
      console.error('Failed to fetch project users:', err);
    }
    
    setLoading(false);
  };

  const handleUserClick = (userId) => {
    navigate(`/user/${userId}`);
  };

  const handleViewUser = (userId, event) => {
    event.stopPropagation(); // Prevent row click
    navigate(`/user/${userId}`);
  };

  const formatRoles = (roles) => {
    if (!roles || roles.length === 0) {
      return <Chip label="No roles" size="small" color="default" variant="outlined" />;
    }
    
    return roles.map((role, index) => (
      <Chip 
        key={index} 
        label={role} 
        size="small" 
        color="primary" 
        variant="outlined"
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    ));
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };



  return (
    <Card sx={{ 
      maxWidth: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <CardContent sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        p: 1
      }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          👥 Project Users ({users.length})
        </Typography>
        
        <Box>
          {error ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              px: 2
            }}>
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Failed to load organization users
              </Typography>
            </Box>
          ) : users.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              py: 4,
              px: 2
            }}>
              <Group sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                No users found in this project
              </Typography>
            </Box>
          ) : (
            <Box>
              <TableContainer component={Paper} sx={{ 
                position: 'relative'
              }}>
                {loading && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                    borderRadius: 1
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Loading users...
                      </Typography>
                    </Box>
                  </Box>
                )}
                <Table stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50' }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50' }}>Email</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50' }}>Roles</TableCell>
                      <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.50' }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow 
                        key={user.id}
                        onClick={() => handleUserClick(user.id)}
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            '& .MuiTableCell-root': {
                              borderBottom: '1px solid',
                              borderColor: 'primary.main'
                            }
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar 
                              sx={{ 
                                width: 40, 
                                height: 40, 
                                bgcolor: 'primary.main',
                                fontSize: '0.875rem'
                              }}
                            >
                              {getInitials(user.name)}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" fontWeight="500">
                                {user.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ID: {user.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {user.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {formatRoles(user.roles)}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View User Details">
                            <IconButton
                              size="small"
                              onClick={(e) => handleViewUser(user.id, e)}
                              sx={{ 
                                color: 'primary.main',
                                '&:hover': {
                                  backgroundColor: 'primary.light',
                                  color: 'white'
                                }
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default OrganizationUsersCard; 